import { Injectable } from '@angular/core';
import QRCode from 'qrcode';

import {
  AccessCode,
  AppUser,
  BulkJob,
  BulkUploadDraft,
  ControllerVendor,
  ControllerVendorDraft,
  DashboardSnapshot,
  GroupAccessCode,
  GroupCodeDraft,
  HospitalityEvent,
  HospitalityEventDraft,
  ManualCodeDraft,
  PmsVendor,
  PmsVendorDraft,
  Space,
  UserRole,
  TenantDraft,
  UserScope,
  VendorBinding,
  VendorConfiguration,
  VendorConfigurationValue,
  userLocationIds,
  userTenantIds
} from '../models/hospitality';
import { ApiResponse } from '../models/api-response';
import {
  CreateLocationRequest,
  CreateControllerVendorRequest,
  CreatePmsVendorRequest,
  CreateUnitRequest,
  BulkCreateLocationsRequest,
  BulkCreateUnitsRequest,
  UpdateControllerVendorRequest,
  UpdatePmsVendorRequest,
  CreateUserRequest,
  UpdateLocationRequest,
  UpdateUnitRequest,
  UpdateUserRequest
} from '../models/dashboard-api';
import {
  ActiveCodesQuery,
  ArchivedCodesQuery,
  BulkJobsQuery,
  ControllerVendorsQuery,
  EventNetworksQuery,
  EventsQuery,
  LocationsQuery,
  PagedControllerVendorResult,
  PagedEventNetworkResult,
  PagedEventResult,
  PagedAccessCodeResult,
  PagedBulkJobResult,
  PagedLocationResult,
  PagedPmsVendorResult,
  PagedSpaceResult,
  PagedTenantResult,
  PagedUserResult,
  PmsVendorsQuery,
  SpaceListQuery,
  TenantsQuery,
  UsersQuery
} from '../models/hospitality-tables';
import { ApiActivityService } from './api-activity.service';
import { HospitalityApiService } from './hospitality-api.service';
import { applyTableQuery, paginateItems } from '../tables/table.utils';
import { AuthService } from '../auth/auth.service';
import { normalizeUserRole } from '../auth/role.utils';

const WIFI_PRIMARY = 'LUMEN-HOSPITALITY';
const WIFI_EVENTS = 'LUMEN-EVENTS';

function wifiPayload(ssid: string, passphrase: string): string {
  return `WIFI:T:WPA;S:${ssid};P:${passphrase};;`;
}

function createUserScope(tenantId: string, locationIds: string[]): UserScope {
  return {
    tenantId,
    locationIds
  };
}

function createControllerBinding(
  vendorId: string,
  suffix: string,
  defaultVlan = 1200,
  verifyTls = true
): VendorBinding {
  if (vendorId === 'cisco-meraki') {
    return {
      vendorId,
      configuration: {
        apiKey: `meraki-${suffix}-key`,
        organizationId: `org_${suffix}`,
        networkId: `net_${suffix}`
      }
    };
  }

  return {
    vendorId,
    configuration: {
      apiUrl: `https://${suffix}.controller.example.com/api`,
      clientId: `controller-${suffix}`,
      clientSecret: `controller-${suffix}-secret`,
      defaultVlan,
      verifyTls
    }
  };
}

function createPmsBinding(vendorId: string, suffix: string, propertyCount = 1): VendorBinding {
  if (vendorId === 'mews') {
    return {
      vendorId,
      configuration: {
        baseUrl: `https://mews-${suffix}.example.com/api`,
        clientToken: `mews-${suffix}-token`,
        enterpriseId: `ent_${suffix}`,
        propertyCount
      }
    };
  }

  return {
    vendorId,
    configuration: {
      endpoint: `https://opera-${suffix}.example.com/api`,
      hotelCode: suffix.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12) || 'HOTEL',
      username: `opera-${suffix}`,
      password: `opera-${suffix}-secret`
    }
  };
}

const BASE_SNAPSHOT: DashboardSnapshot = {
  tenants: [
    {
      id: 'aurora-collection',
      name: 'Aurora Collection',
      brand: 'Luxury city stays',
      sla: '99.95%',
      properties: 2,
      dpskCoverage: 92,
      openAlerts: 2
    },
    {
      id: 'north-harbor-venues',
      name: 'North Harbor Venues',
      brand: 'MICE and resort operations',
      sla: '99.90%',
      properties: 1,
      dpskCoverage: 84,
      openAlerts: 1
    }
  ],
  locations: [
    {
      id: 'palazzo-amare-roma',
      tenantId: 'aurora-collection',
      name: 'Palazzo Amare Roma',
      city: 'Rome',
      category: 'hotel',
      ssid: WIFI_PRIMARY,
      pms: createPmsBinding('oracle-opera', 'roma-01'),
      controller: createControllerBinding('ruckus-one', 'roma-01', 1205),
      tvIntegration: true,
      emailIntegration: true,
      state: 'active',
      dailyCheckIns: 74,
      activeGuests: 118,
      bulkQueue: 2,
      note: 'Room PAN ready with TV welcome screen and mail fallback.',
      timezone: 'Europe/Rome'
    },
    {
      id: 'lago-blu-resort',
      tenantId: 'aurora-collection',
      name: 'Lago Blu Resort',
      city: 'Como',
      category: 'resort',
      ssid: WIFI_PRIMARY,
      pms: createPmsBinding('mews', 'como-01'),
      controller: createControllerBinding('ruckus-one', 'como-01', 2401),
      tvIntegration: false,
      emailIntegration: true,
      state: 'pending',
      dailyCheckIns: 42,
      activeGuests: 87,
      bulkQueue: 1,
      note: 'Primary delivery is email because TV middleware is not connected yet.',
      timezone: 'Europe/Rome'
    },
    {
      id: 'officine-forum-milano',
      tenantId: 'north-harbor-venues',
      name: 'Officine Forum Milano',
      city: 'Milan',
      category: 'conference',
      ssid: WIFI_EVENTS,
      pms: createPmsBinding('mews', 'milano-01', 3),
      controller: createControllerBinding('cisco-meraki', 'milano-01'),
      tvIntegration: false,
      emailIntegration: true,
      state: 'active',
      dailyCheckIns: 18,
      activeGuests: 244,
      bulkQueue: 7,
      note: 'Designed for bulk onboarding, shared VLAN groups, and event hall casting.',
      timezone: 'Europe/Rome'
    }
  ],
  spaces: [
    {
      id: 'room-205',
      locationId: 'palazzo-amare-roma',
      name: 'Room 205',
      type: 'room',
      vlanId: 1205,
      serviceVlanId: null,
      networkId: 'net_roma_01',
      ssid: WIFI_PRIMARY,
      panMode: 'private',
      capacity: 4,
      assignment: 'room',
      status: 'available'
    },
    {
      id: 'room-206',
      locationId: 'palazzo-amare-roma',
      name: 'Room 206',
      type: 'room',
      vlanId: 1206,
      serviceVlanId: null,
      networkId: 'net_roma_01',
      ssid: WIFI_PRIMARY,
      panMode: 'private',
      capacity: 4,
      assignment: 'room',
      status: 'available'
    },
    {
      id: 'roma-lounge-bar',
      locationId: 'palazzo-amare-roma',
      name: 'Lounge Bar',
      type: 'public-area',
      vlanId: 9100,
      serviceVlanId: 9100,
      networkId: 'net_roma_01',
      ssid: WIFI_PRIMARY,
      panMode: 'public',
      capacity: 40,
      assignment: 'common',
      status: 'available'
    },
    {
      id: 'sala-camelia',
      locationId: 'palazzo-amare-roma',
      name: 'Sala Camelia',
      type: 'event-hall',
      vlanId: 3100,
      serviceVlanId: 3100,
      networkId: 'net_roma_01',
      ssid: WIFI_EVENTS,
      panMode: 'shared-group',
      capacity: 120,
      assignment: 'group',
      status: 'available'
    },
    {
      id: 'suite-401',
      locationId: 'lago-blu-resort',
      name: 'Suite 401',
      type: 'room',
      vlanId: 2401,
      serviceVlanId: null,
      networkId: 'net_como_01',
      ssid: WIFI_PRIMARY,
      panMode: 'private',
      capacity: 5,
      assignment: 'room',
      status: 'occupied'
    },
    {
      id: 'spa-lobby',
      locationId: 'lago-blu-resort',
      name: 'Spa Lobby',
      type: 'public-area',
      vlanId: 9200,
      serviceVlanId: 9200,
      networkId: 'net_como_01',
      ssid: WIFI_PRIMARY,
      panMode: 'public',
      capacity: 55,
      assignment: 'common',
      status: 'available'
    },
    {
      id: 'hall-a',
      locationId: 'officine-forum-milano',
      name: 'Hall A',
      type: 'event-hall',
      vlanId: 5300,
      serviceVlanId: 5300,
      networkId: 'net_milano_01',
      ssid: WIFI_EVENTS,
      panMode: 'shared-group',
      capacity: 350,
      assignment: 'group',
      status: 'available'
    },
    {
      id: 'networking-lounge',
      locationId: 'officine-forum-milano',
      name: 'Networking Lounge',
      type: 'public-area',
      vlanId: 9300,
      serviceVlanId: 9300,
      networkId: 'net_milano_01',
      ssid: WIFI_PRIMARY,
      panMode: 'public',
      capacity: 180,
      assignment: 'common',
      status: 'available'
    }
  ],
  codes: [
    {
      id: 'DPSK-1205-ROSSI',
      guestName: 'Giulia Rossi',
      email: 'giulia.rossi@example.com',
      locationId: 'palazzo-amare-roma',
      spaceId: 'room-205',
      source: 'pms',
      deliveryChannels: ['tv', 'email'],
      status: 'active',
      passphrase: 'LMN-1205-XQ9K',
      validFrom: '2026-05-14T11:00',
      validUntil: '2026-05-17T10:00',
      devicesAllowed: 5,
      devicesConnected: 2,
      lastSeenAt: '2026-05-14T14:12',
      qrPayload: wifiPayload(WIFI_PRIMARY, 'LMN-1205-XQ9K'),
      bookingReference: 'OPR-205-7841',
      category: null,
      serviceMode: false,
      tags: ['room-205', 'checkin']
    },
    {
      id: 'DPSK-1206-BIANCHI',
      guestName: 'Stefano Bianchi',
      email: 'stefano.bianchi@example.com',
      locationId: 'palazzo-amare-roma',
      spaceId: 'room-206',
      source: 'pms',
      deliveryChannels: ['tv', 'dashboard'],
      status: 'active',
      passphrase: 'LMN-1206-MP3R',
      validFrom: '2026-05-14T12:30',
      validUntil: '2026-05-16T10:00',
      devicesAllowed: 4,
      devicesConnected: 1,
      lastSeenAt: '2026-05-14T13:42',
      qrPayload: wifiPayload(WIFI_PRIMARY, 'LMN-1206-MP3R'),
      bookingReference: 'OPR-206-7850',
      category: null,
      serviceMode: false,
      tags: ['room-206', 'checkin']
    },
    {
      id: 'DPSK-BAR-CONTI',
      guestName: 'Marta Conti',
      email: 'marta.conti@example.com',
      locationId: 'palazzo-amare-roma',
      spaceId: 'roma-lounge-bar',
      source: 'reception',
      deliveryChannels: ['dashboard', 'print'],
      status: 'active',
      passphrase: 'LMN-9100-BAR5',
      validFrom: '2026-05-14T14:00',
      validUntil: '2026-05-14T23:59',
      devicesAllowed: 2,
      devicesConnected: 1,
      lastSeenAt: '2026-05-14T14:28',
      qrPayload: wifiPayload(WIFI_PRIMARY, 'LMN-9100-BAR5'),
      bookingReference: 'WALK-IN-312',
      category: 'visitor',
      serviceMode: true,
      tags: ['bar', 'temporary']
    },
    {
      id: 'DPSK-CAMELIA-ALFA',
      guestName: 'Apple Summit Guest 01',
      email: 'guest01@apple.example',
      locationId: 'officine-forum-milano',
      spaceId: 'hall-a',
      source: 'bulk',
      deliveryChannels: ['email'],
      status: 'active',
      passphrase: 'LMN-5300-EVT1',
      validFrom: '2026-05-14T08:00',
      validUntil: '2026-05-15T21:00',
      devicesAllowed: 3,
      devicesConnected: 2,
      lastSeenAt: '2026-05-14T14:07',
      qrPayload: wifiPayload(WIFI_EVENTS, 'LMN-5300-EVT1'),
      bookingReference: 'APPLE-SUMMIT-001',
      category: 'staff',
      serviceMode: false,
      tags: ['apple', 'shared-vlan']
    },
    {
      id: 'DPSK-SUITE-401',
      guestName: 'Marco Villa',
      email: 'marco.villa@example.com',
      locationId: 'lago-blu-resort',
      spaceId: 'suite-401',
      source: 'pms',
      deliveryChannels: ['email'],
      status: 'scheduled',
      passphrase: 'LMN-2401-LAKE',
      validFrom: '2026-05-15T15:00',
      validUntil: '2026-05-18T10:30',
      devicesAllowed: 6,
      devicesConnected: 0,
      qrPayload: wifiPayload(WIFI_PRIMARY, 'LMN-2401-LAKE'),
      bookingReference: 'PROTEL-401-992',
      category: null,
      serviceMode: false,
      tags: ['suite-401', 'pre-arrival']
    },
    {
      id: 'DPSK-SPA-SERRA',
      guestName: 'Laura Serra',
      email: 'laura.serra@example.com',
      locationId: 'lago-blu-resort',
      spaceId: 'spa-lobby',
      source: 'reception',
      deliveryChannels: ['dashboard'],
      status: 'revoked',
      passphrase: 'LMN-9200-SPA9',
      validFrom: '2026-05-13T09:00',
      validUntil: '2026-05-14T18:00',
      devicesAllowed: 1,
      devicesConnected: 0,
      qrPayload: wifiPayload(WIFI_PRIMARY, 'LMN-9200-SPA9'),
      bookingReference: 'SPA-DAY-114',
      category: 'iot',
      serviceMode: true,
      tags: ['spa', 'external']
    },
    {
      id: 'DPSK-CAMELIA-WED',
      guestName: 'Wedding Planner',
      email: 'planner@evento.example',
      locationId: 'palazzo-amare-roma',
      spaceId: 'sala-camelia',
      source: 'bulk',
      deliveryChannels: ['email', 'print'],
      status: 'expired',
      passphrase: 'LMN-3100-WED7',
      validFrom: '2026-05-11T08:00',
      validUntil: '2026-05-12T23:00',
      devicesAllowed: 3,
      devicesConnected: 0,
      qrPayload: wifiPayload(WIFI_EVENTS, 'LMN-3100-WED7'),
      bookingReference: 'WEDDING-CAMELIA',
      category: 'visitor',
      serviceMode: false,
      tags: ['event', 'shared-vlan']
    },
    {
      id: 'DPSK-SUPPLIER-9300',
      guestName: 'Supplier Catering',
      email: 'support@catering.example',
      locationId: 'officine-forum-milano',
      spaceId: 'networking-lounge',
      source: 'reception',
      deliveryChannels: ['dashboard'],
      status: 'active',
      passphrase: 'LMN-9300-CAT8',
      validFrom: '2026-05-14T07:30',
      validUntil: '2026-05-14T20:00',
      devicesAllowed: 2,
      devicesConnected: 1,
      lastSeenAt: '2026-05-14T10:52',
      qrPayload: wifiPayload(WIFI_PRIMARY, 'LMN-9300-CAT8'),
      bookingReference: 'SUP-552',
      category: 'staff',
      serviceMode: true,
      tags: ['vendor', 'common-space']
    }
  ],
  events: [
    {
      id: 'evt-camelia-design-week',
      tenantId: 'aurora-collection',
      locationId: 'palazzo-amare-roma',
      name: 'Rome Design Week Reception',
      spaceId: 'sala-camelia',
      networkId: 'net_roma_01',
      ssid: 'LUMEN-DESIGN-WEEK',
      validFrom: '2026-09-15T07:00',
      validUntil: '2026-09-16T23:00',
      isolation: true,
      policyId: null,
      code: 'LMN-3100-DESIGN',
      status: 'scheduled',
      contactName: 'Event Manager',
      contactEmail: 'events@example.com',
      deliveryChannels: ['email', 'dashboard', 'print']
    }
  ],
  bulkJobs: [
    {
      id: 'BLK-240514-01',
      locationId: 'officine-forum-milano',
      fileName: 'apple-summit.csv',
      requestedBy: 'Elena Ferri',
      createdAt: '2026-05-14T08:15',
      total: 180,
      processed: 124,
      mode: 'shared-vlan',
      queueStatus: 'running',
      targetVlan: 5300
    },
    {
      id: 'BLK-240513-02',
      locationId: 'palazzo-amare-roma',
      fileName: 'wedding-camelia.csv',
      requestedBy: 'Paolo Mancini',
      createdAt: '2026-05-13T16:40',
      total: 48,
      processed: 48,
      mode: 'isolated',
      queueStatus: 'completed',
      targetVlan: 3100
    },
    {
      id: 'BLK-240514-03',
      locationId: 'lago-blu-resort',
      fileName: 'spa-day-pass.csv',
      requestedBy: 'Sara Romano',
      createdAt: '2026-05-14T09:25',
      total: 12,
      processed: 12,
      mode: 'shared-vlan',
      queueStatus: 'completed',
      targetVlan: 9200
    }
  ],
  users: [
    {
      id: 'luca-gentili',
      name: 'Luca Gentili',
      email: 'luca.gentili@lumen.local',
      role: 'platform-admin',
      scopes: [
        createUserScope('aurora-collection', ['palazzo-amare-roma', 'lago-blu-resort']),
        createUserScope('north-harbor-venues', ['officine-forum-milano'])
      ],
      status: 'active'
    },
    {
      id: 'sara-romano',
      name: 'Sara Romano',
      email: 'sara.romano@aurora.local',
      role: 'tenant-admin',
      scopes: [createUserScope('aurora-collection', ['palazzo-amare-roma', 'lago-blu-resort'])],
      status: 'active'
    },
    {
      id: 'elena-ferri',
      name: 'Elena Ferri',
      email: 'elena.ferri@forum.local',
      role: 'reception',
      scopes: [createUserScope('north-harbor-venues', ['officine-forum-milano'])],
      status: 'active'
    },
    {
      id: 'paolo-mancini',
      name: 'Paolo Mancini',
      email: 'paolo.mancini@aurora.local',
      role: 'operations',
      scopes: [createUserScope('aurora-collection', ['palazzo-amare-roma'])],
      status: 'invited'
    }
  ],
  alerts: [
    {
      id: 'alert-queue-mice',
      locationId: 'officine-forum-milano',
      severity: 'high',
      title: 'Bulk queue close to vendor rate limit',
      description:
        'The running MICE import is consuming the shared VLAN queue faster than the safe controller platform threshold.'
    },
    {
      id: 'alert-tv-206',
      locationId: 'palazzo-amare-roma',
      severity: 'medium',
      title: 'Room 206 TV sync delayed',
      description:
        'The DPSK exists on the network controller, but the IPTV welcome card is still waiting for middleware acknowledgment.'
    },
    {
      id: 'alert-como-fallback',
      locationId: 'lago-blu-resort',
      severity: 'medium',
      title: 'Mail fallback active on resort',
      description:
        'Because TV middleware is not connected, all guest keys are currently distributed by email or reception.'
    }
  ]
};

const MOCK_SNAPSHOT: DashboardSnapshot = enrichMockSnapshot(BASE_SNAPSHOT);

const BASE_CONTROLLER_VENDORS: ControllerVendor[] = [
  {
    id: 'ruckus-one',
    name: 'Ruckus One',
    fields: {
      apiUrl: 'string',
      clientId: 'string',
      clientSecret: 'password',
      defaultVlan: 'number',
      verifyTls: 'boolean'
    }
  },
  {
    id: 'cisco-meraki',
    name: 'Cisco Meraki',
    fields: {
      apiKey: 'password',
      organizationId: 'string',
      networkId: 'string'
    }
  }
];

const BASE_PMS_VENDORS: PmsVendor[] = [
  {
    id: 'oracle-opera',
    name: 'Oracle Opera',
    fields: {
      endpoint: 'string',
      hotelCode: 'string',
      username: 'string',
      password: 'password'
    }
  },
  {
    id: 'mews',
    name: 'Mews',
    fields: {
      baseUrl: 'string',
      clientToken: 'password',
      enterpriseId: 'string',
      propertyCount: 'number'
    }
  }
];

function enrichMockSnapshot(snapshot: DashboardSnapshot): DashboardSnapshot {
  const auroraLocations = createAuroraOverflowLocations();
  const palazzoExpansion = createPalazzoExpansion();
  const partnerPortfolio = createPartnerPortfolio();

  return {
    ...snapshot,
    tenants: [...snapshot.tenants, ...partnerPortfolio.tenants],
    locations: [...snapshot.locations, ...auroraLocations.locations, ...partnerPortfolio.locations],
    spaces: [...snapshot.spaces, ...palazzoExpansion.spaces, ...auroraLocations.spaces, ...partnerPortfolio.spaces],
    codes: [...snapshot.codes, ...palazzoExpansion.activeCodes, ...palazzoExpansion.archivedCodes],
    bulkJobs: [...snapshot.bulkJobs, ...palazzoExpansion.bulkJobs],
    users: [...snapshot.users, ...partnerPortfolio.users],
    alerts: snapshot.alerts
  };
}

function createAuroraOverflowLocations(): {
  locations: DashboardSnapshot['locations'];
  spaces: DashboardSnapshot['spaces'];
} {
  const cities = ['Florence', 'Venice', 'Naples', 'Turin', 'Bari', 'Bologna', 'Verona', 'Genoa'];

  const locations = Array.from({ length: 8 }, (_, index) => {
    const sequence = index + 1;

    return {
      id: `aurora-city-hub-${sequence}`,
      tenantId: 'aurora-collection',
      name: `Aurora City Hub ${sequence}`,
      city: cities[index] ?? `City ${sequence}`,
      category: sequence % 3 === 0 ? 'resort' : 'hotel',
      ssid: sequence % 3 === 0 ? WIFI_EVENTS : WIFI_PRIMARY,
      pms: createPmsBinding(sequence % 2 === 0 ? 'mews' : 'oracle-opera', `aurora-${sequence}`),
      controller: createControllerBinding(
        sequence % 2 === 0 ? 'cisco-meraki' : 'ruckus-one',
        `aurora-${sequence}`,
        3400 + index
      ),
      tvIntegration: sequence % 2 === 0,
      emailIntegration: true,
      state: sequence % 4 === 0 ? 'pending' : 'active',
      dailyCheckIns: 20 + sequence * 4,
      activeGuests: 45 + sequence * 9,
      bulkQueue: sequence % 3,
      note: `Secondary property ${sequence} ready for demo pagination and tenant filtering.`,
      timezone: 'Europe/Rome'
    } satisfies DashboardSnapshot['locations'][number];
  });

  const spaces = locations.map((location, index) => ({
    id: `${location.id}-room-101`,
    locationId: location.id,
    name: `Room ${index + 1}01`,
    type: 'room',
    vlanId: 3400 + index,
    serviceVlanId: null,
    networkId: `net_${location.id}`,
    ssid: WIFI_PRIMARY,
    panMode: 'private',
    capacity: 3,
    assignment: 'room',
    status: 'available'
  } satisfies DashboardSnapshot['spaces'][number]));

  return { locations, spaces };
}

function createPartnerPortfolio(): {
  tenants: DashboardSnapshot['tenants'];
  locations: DashboardSnapshot['locations'];
  spaces: DashboardSnapshot['spaces'];
  users: DashboardSnapshot['users'];
} {
  const tenantCount = 7;

  const tenants = Array.from({ length: tenantCount }, (_, index) => {
    const sequence = index + 1;

    return {
      id: `partner-tenant-${sequence}`,
      name: `Partner Tenant ${sequence}`,
      brand: `Demo hospitality brand ${sequence}`,
      sla: '99.90%',
      properties: 1,
      dpskCoverage: 78 + sequence,
      openAlerts: 0
    } satisfies DashboardSnapshot['tenants'][number];
  });

  const locations = tenants.map((tenant, index) => ({
    id: `${tenant.id}-location`,
    tenantId: tenant.id,
    name: `Partner Venue ${index + 1}`,
    city: ['Berlin', 'Madrid', 'Paris', 'Lisbon', 'Prague', 'Vienna', 'Warsaw'][index] ?? `City ${index + 1}`,
    category: index % 2 === 0 ? 'conference' : 'hotel',
    ssid: index % 2 === 0 ? WIFI_EVENTS : WIFI_PRIMARY,
    pms: createPmsBinding(index % 2 === 0 ? 'mews' : 'oracle-opera', `partner-${index + 1}`),
    controller: createControllerBinding(
      index % 2 === 0 ? 'cisco-meraki' : 'ruckus-one',
      `partner-${index + 1}`,
      4300 + index
    ),
    tvIntegration: index % 2 === 1,
    emailIntegration: true,
    state: 'active',
    dailyCheckIns: 10 + index * 3,
    activeGuests: 35 + index * 6,
    bulkQueue: index % 2,
    note: `Partner location ${index + 1} added to exercise pagination.`,
    timezone: 'Europe/Rome'
  } satisfies DashboardSnapshot['locations'][number]));

  const spaces = locations.map((location, index) => ({
    id: `${location.id}-room-201`,
    locationId: location.id,
    name: `Room 20${index + 1}`,
    type: 'room',
    vlanId: 4300 + index,
    serviceVlanId: null,
    networkId: `net_${location.id}`,
    ssid: WIFI_PRIMARY,
    panMode: 'private',
    capacity: 2,
    assignment: 'room',
    status: 'available'
  } satisfies DashboardSnapshot['spaces'][number]));

  const users = tenants.map((tenant, index) => ({
    id: `partner-user-${index + 1}`,
    name: `Partner User ${index + 1}`,
    email: `partner.user${index + 1}@lumenhospitality.demo`,
    role: index % 2 === 0 ? 'tenant-admin' : 'operations',
    scopes: [createUserScope(tenant.id, [locations[index].id])],
    status: index % 3 === 0 ? 'invited' : 'active'
  } satisfies DashboardSnapshot['users'][number]));

  return { tenants, locations, spaces, users };
}

function createPalazzoExpansion(): {
  spaces: DashboardSnapshot['spaces'];
  activeCodes: DashboardSnapshot['codes'];
  archivedCodes: DashboardSnapshot['codes'];
  bulkJobs: DashboardSnapshot['bulkJobs'];
} {
  const generatedRooms = Array.from({ length: 10 }, (_, index) => {
    const roomNumber = 207 + index;

    return {
      id: `room-${roomNumber}`,
      locationId: 'palazzo-amare-roma',
      name: `Room ${roomNumber}`,
      type: 'room',
      vlanId: 1200 + roomNumber,
      serviceVlanId: null,
      networkId: 'net_roma_01',
      ssid: WIFI_PRIMARY,
      panMode: 'private',
      capacity: roomNumber % 3 === 0 ? 3 : 4,
      assignment: 'room',
      status: 'available'
    } satisfies DashboardSnapshot['spaces'][number];
  });

  const generatedSharedSpaces: DashboardSnapshot['spaces'] = [
    {
      id: 'roof-garden',
      locationId: 'palazzo-amare-roma',
      name: 'Roof Garden',
      type: 'public-area',
      vlanId: 9105,
      serviceVlanId: 9105,
      networkId: 'net_roma_01',
      ssid: WIFI_PRIMARY,
      panMode: 'public',
      capacity: 60,
      assignment: 'common',
      status: 'available'
    },
    {
      id: 'meeting-suite-nova',
      locationId: 'palazzo-amare-roma',
      name: 'Meeting Suite Nova',
      type: 'event-hall',
      vlanId: 3110,
      serviceVlanId: 3110,
      networkId: 'net_roma_01',
      ssid: WIFI_EVENTS,
      panMode: 'shared-group',
      capacity: 48,
      assignment: 'group',
      status: 'available'
    }
  ];

  const allGeneratedSpaces = [...generatedRooms, ...generatedSharedSpaces];

  const activeCodes = generatedRooms.slice(0, 8).map((space, index) => {
    const guestNumber = String(index + 1).padStart(2, '0');
    const passphrase = `LMN-${space.vlanId}-A${guestNumber}`;

    return {
      id: `DPSK-PALAZZO-ACT-${guestNumber}`,
      guestName: `Aurora Guest ${guestNumber}`,
      email: `aurora.guest${guestNumber}@example.com`,
      locationId: 'palazzo-amare-roma',
      spaceId: space.id,
      source: index % 3 === 0 ? 'pms' : index % 3 === 1 ? 'reception' : 'bulk',
      category: index % 3 === 1 ? 'guest' : null,
      deliveryChannels: index % 2 === 0 ? ['email', 'dashboard'] : ['tv', 'email'],
      status: index % 4 === 0 ? 'scheduled' : 'active',
      passphrase,
      validFrom: `2026-05-${String(14 + (index % 2)).padStart(2, '0')}T1${index % 6}:00`,
      validUntil: `2026-05-${String(18 + (index % 3)).padStart(2, '0')}T10:00`,
      devicesAllowed: 3 + (index % 3),
      devicesConnected: index % 3,
      lastSeenAt: `2026-05-14T1${index}:20`,
      qrPayload: wifiPayload(space.ssid ?? WIFI_PRIMARY, passphrase),
      bookingReference: `AURORA-PAL-${guestNumber}`,
      serviceMode: false,
      tags: ['room', 'pagination-demo']
    } satisfies DashboardSnapshot['codes'][number];
  });

  const archivedCodes = generatedRooms.slice(2, 10).map((space, index) => {
    const guestNumber = String(index + 1).padStart(2, '0');
    const passphrase = `LMN-${space.vlanId}-R${guestNumber}`;

    return {
      id: `DPSK-PALAZZO-ARC-${guestNumber}`,
      guestName: `Archived Guest ${guestNumber}`,
      email: `archived.guest${guestNumber}@example.com`,
      locationId: 'palazzo-amare-roma',
      spaceId: space.id,
      source: index % 2 === 0 ? 'reception' : 'bulk',
      category: index % 2 === 0 ? 'guest' : null,
      deliveryChannels: index % 2 === 0 ? ['dashboard'] : ['print', 'email'],
      status: index % 3 === 0 ? 'revoked' : 'expired',
      passphrase,
      validFrom: `2026-05-${String(7 + index).padStart(2, '0')}T08:00`,
      validUntil: `2026-05-${String(8 + index).padStart(2, '0')}T18:00`,
      devicesAllowed: 2,
      devicesConnected: 0,
      qrPayload: wifiPayload(space.ssid ?? WIFI_PRIMARY, passphrase),
      bookingReference: `ARCHIVE-PAL-${guestNumber}`,
      serviceMode: false,
      tags: ['archived', 'pagination-demo']
    } satisfies DashboardSnapshot['codes'][number];
  });

  const bulkJobs = Array.from({ length: 7 }, (_, index) => ({
    id: `BLK-PAL-${String(index + 1).padStart(2, '0')}`,
    locationId: 'palazzo-amare-roma',
    fileName: `palazzo-batch-${index + 1}.csv`,
    requestedBy: index % 2 === 0 ? 'Front Desk Team' : 'Night Audit',
    createdAt: `2026-05-${String(6 + index).padStart(2, '0')}T0${index % 7}:30`,
    total: 16 + index * 3,
    processed: index % 3 === 0 ? 8 + index * 2 : 16 + index * 3,
    mode: index % 2 === 0 ? 'shared-vlan' : 'isolated',
    queueStatus: index % 3 === 0 ? 'running' : index % 3 === 1 ? 'queued' : 'completed',
    targetVlan: index % 2 === 0 ? 3100 : 1207 + index
  } satisfies DashboardSnapshot['bulkJobs'][number]));

  return {
    spaces: allGeneratedSpaces,
    activeCodes,
    archivedCodes,
    bulkJobs
  };
}

@Injectable({ providedIn: 'root' })
export class MockHospitalityApiService extends HospitalityApiService {
  constructor(
    private readonly activity: ApiActivityService = new ApiActivityService(),
    private readonly auth: AuthService | null = null
  ) {
    super();
  }

  private snapshot: DashboardSnapshot = structuredClone(MOCK_SNAPSHOT);
  private controllerVendors: ControllerVendor[] = structuredClone(BASE_CONTROLLER_VENDORS);
  private pmsVendors: PmsVendor[] = structuredClone(BASE_PMS_VENDORS);

  override async loadDashboardSnapshot(tenantId: string): Promise<ApiResponse<DashboardSnapshot>> {
    const visibleSnapshot = this.visibleSnapshot();
    const tenantExists = visibleSnapshot.tenants.some((tenant) => tenant.id === tenantId);
    const payload = tenantExists ? structuredClone(visibleSnapshot) : structuredClone(this.emptySnapshot());

    return this.respond(payload, 260);
  }

  override replaceSnapshot(snapshot: DashboardSnapshot): void {
    this.snapshot = structuredClone(snapshot);
  }

  async listLocationUnits(query: SpaceListQuery): Promise<ApiResponse<PagedSpaceResult>> {
    const visibleSnapshot = this.visibleSnapshot();
    const filtered = visibleSnapshot.spaces.filter((space) => {
      if (!this.locationBelongsToTenant(query.locationId, query.tenantId)) {
        return false;
      }

      if (space.locationId !== query.locationId) {
        return false;
      }

      if (query.segment === 'room') {
        return space.type === 'room';
      }

      if (query.segment === 'shared') {
        return space.type !== 'room';
      }

      return true;
    });

    return this.respond(
      structuredClone(
        applyTableQuery(filtered, query, {
          name: (space) => space.name,
          type: (space) => space.type,
          vlan: (space) => space.vlanId,
          ssid: (space) => this.locationSsid(space.locationId),
          panMode: (space) => space.panMode,
          capacity: (space) => space.capacity
        })
      ),
      160
    );
  }

  override async listLocationSpaces(query: SpaceListQuery): Promise<ApiResponse<PagedSpaceResult>> {
    return this.listLocationUnits(query);
  }

  override async getActiveSpaceCode(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<AccessCode>> {
    const code = this.snapshot.codes.find(
      (entry) =>
        entry.locationId === locationId &&
        entry.spaceId === spaceId &&
        entry.status === 'active' &&
        this.locationBelongsToTenant(locationId, tenantId)
    );

    return this.respond(code ?? null, 120, code ? undefined : {
      errorCode: 404,
      errorMessage: 'No active access code exists for the selected space.'
    });
  }

  override async getActiveSpaceNetworkQr(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<{ ssid: string; passphrase: string; qrPayload: string }>> {
    const response = await this.getActiveSpaceCode(tenantId, locationId, spaceId);
    const code = response.data;

    if (!code) {
      return this.respond(null, 80, {
        errorCode: response.errorCode,
        errorMessage: response.errorMessage
      });
    }

    return this.respond({
      ssid: this.locationSsid(locationId),
      passphrase: code.passphrase,
      qrPayload: code.qrPayload
    }, 80);
  }

  override async listActiveLocationCodes(query: ActiveCodesQuery): Promise<ApiResponse<PagedAccessCodeResult>> {
    const visibleSnapshot = this.visibleSnapshot();
    const normalizedSearch = query.search.trim().toLowerCase();
    const filtered = visibleSnapshot.codes.filter((code) => {
      if (!this.locationBelongsToTenant(query.locationId, query.tenantId)) {
        return false;
      }

      if (code.locationId !== query.locationId) {
        return false;
      }

      if (visibleSnapshot.spaces.find((space) => space.id === code.spaceId)?.type === 'event-hall') {
        return false;
      }

      if (code.status !== 'active' && code.status !== 'scheduled') {
        return false;
      }

      if (query.sourceFilter !== 'all' && code.source !== query.sourceFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return (
        code.guestName.toLowerCase().includes(normalizedSearch) ||
        code.bookingReference.toLowerCase().includes(normalizedSearch) ||
        code.passphrase.toLowerCase().includes(normalizedSearch)
      );
    });

    return this.respond(
      structuredClone(
        applyTableQuery(filtered, query, {
          guest: (code) => `${code.guestName} ${code.bookingReference}`,
          space: (code) => this.spaceName(code.spaceId),
          validity: (code) => new Date(code.validUntil),
          source: (code) => code.source,
          devices: (code) => code.devicesConnected + code.devicesAllowed / 100
        })
      ),
      170
    );
  }

  override async listLocationEvents(query: EventsQuery): Promise<ApiResponse<PagedEventResult>> {
    const normalizedSearch = query.search?.trim().toLowerCase() ?? '';
    const filtered = this.visibleSnapshot().events.filter((event) => {
      if (!this.locationBelongsToTenant(query.locationId, query.tenantId)) return false;
      if (event.locationId !== query.locationId) return false;
      if (query.status && event.status !== query.status) return false;
      return !normalizedSearch || event.name.toLowerCase().includes(normalizedSearch);
    });

    return this.respond(
      structuredClone(
        applyTableQuery(filtered, query, {
          name: (event) => event.name,
          validity: (event) => new Date(event.validFrom),
          space: (event) => this.spaceName(event.spaceId),
          status: (event) => event.status
        })
      ),
      160
    );
  }

  async listActiveUnitCode(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<AccessCode>> {
    const visibleSnapshot = this.visibleSnapshot();
    const code =
      visibleSnapshot.codes.find(
        (entry) =>
          this.locationBelongsToTenant(locationId, tenantId) &&
          entry.locationId === locationId &&
          entry.spaceId === spaceId &&
          (entry.status === 'active' || entry.status === 'scheduled')
      ) ?? null;

    return this.respond(code, 120);
  }

  async getActiveUnitNetworkQr(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<{ ssid: string; passphrase: string; qrPayload: string }>> {
    const visibleSnapshot = this.visibleSnapshot();
    const activeCodeResponse = await this.listActiveUnitCode(tenantId, locationId, spaceId);
    const code = activeCodeResponse.data;
    const unit = visibleSnapshot.spaces.find(
      (space) => space.id === spaceId && space.locationId === locationId
    );

    return this.respond(
      code && unit
        ? {
            ssid: this.locationSsid(locationId),
            passphrase: code.passphrase,
            qrPayload: code.qrPayload
          }
        : null,
      110
    );
  }

  override async listArchivedLocationCodes(
    query: ArchivedCodesQuery
  ): Promise<ApiResponse<PagedAccessCodeResult>> {
    const visibleSnapshot = this.visibleSnapshot();
    const normalizedSearch = query.search.trim().toLowerCase();
    const filtered = visibleSnapshot.codes.filter((code) => {
      if (!this.locationBelongsToTenant(query.locationId, query.tenantId)) {
        return false;
      }

      if (code.locationId !== query.locationId) {
        return false;
      }

      if (visibleSnapshot.spaces.find((space) => space.id === code.spaceId)?.type === 'event-hall') {
        return false;
      }

      if (code.status !== 'expired' && code.status !== 'revoked') {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return (
        code.guestName.toLowerCase().includes(normalizedSearch) ||
        code.bookingReference.toLowerCase().includes(normalizedSearch) ||
        code.passphrase.toLowerCase().includes(normalizedSearch)
      );
    });

    return this.respond(
      structuredClone(
        applyTableQuery(filtered, query, {
          guest: (code) => `${code.guestName} ${code.bookingReference}`,
          source: (code) => code.source,
          validity: (code) => new Date(code.validUntil)
        })
      ),
      170
    );
  }

  override async listLocationEventNetworks(
    query: EventNetworksQuery
  ): Promise<ApiResponse<PagedEventNetworkResult>> {
    if (!this.locationBelongsToTenant(query.locationId, query.tenantId)) {
      return this.respond(paginateItems([], query.page, query.pageSize), 140);
    }

    const reserved = new Set(
      this.visibleSnapshot().events
        .filter((event) => event.locationId === query.locationId && event.status !== 'disabled' && event.status !== 'expired')
        .map((event) => event.networkId)
    );
    const available = Array.from({ length: 12 }, (_, index) => {
      const suffix = String(index + 1).padStart(2, '0');
      return {
        networkId: `net_${query.locationId}_event_${suffix}`,
        name: `Event network ${suffix}`,
        ssid: `LUMEN-EVENT-${suffix}`
      };
    }).filter((network) => !reserved.has(network.networkId));

    return this.respond(paginateItems(available, query.page, query.pageSize), 140);
  }

  override async listLocationBulkJobs(query: BulkJobsQuery): Promise<ApiResponse<PagedBulkJobResult>> {
    const filtered = this.visibleSnapshot().bulkJobs.filter((job) => job.locationId === query.locationId);

    return this.respond(
      structuredClone(
        applyTableQuery(filtered, query, {
          job: (job) => `${job.fileName} ${job.requestedBy}`,
          createdAt: (job) => new Date(job.createdAt),
          vlan: (job) => job.targetVlan ?? -1,
          state: (job) => job.queueStatus
        })
      ),
      160
    );
  }

  override async listTenants(query: TenantsQuery): Promise<ApiResponse<PagedTenantResult>> {
    return this.respond(
      structuredClone(
        applyTableQuery(this.visibleSnapshot().tenants, query, {
          name: (tenant) => tenant.name
        })
      ),
      140
    );
  }

  override async listUsers(query: UsersQuery): Promise<ApiResponse<PagedUserResult>> {
    return this.respond(
      structuredClone(
        applyTableQuery(this.visibleSnapshot().users, query, {
          name: (user) => user.name,
          tenants: (user) => this.userTenantNames(user),
          locations: (user) => this.userLocationNames(user)
        })
      ),
      140
    );
  }

  override async listLocations(query: LocationsQuery): Promise<ApiResponse<PagedLocationResult>> {
    const filtered = this.visibleSnapshot().locations.filter(
      (location) => location.tenantId === query.tenantId
    );

    return this.respond(
      structuredClone(
        applyTableQuery(filtered, query, {
          name: (location) => location.name,
          pms: (location) => this.pmsVendorName(location.pms),
          controller: (location) => this.controllerVendorName(location.controller)
        })
      ),
      150
    );
  }

  override async listControllerVendors(
    query: ControllerVendorsQuery
  ): Promise<ApiResponse<PagedControllerVendorResult>> {
    const visibleVendors = this.canViewVendorCatalogs() ? this.controllerVendors : [];

    return this.respond(
      structuredClone(
        applyTableQuery(visibleVendors, query, {
          name: (vendor) => vendor.name
        })
      ),
      130
    );
  }

  override async listPmsVendors(query: PmsVendorsQuery): Promise<ApiResponse<PagedPmsVendorResult>> {
    const visibleVendors = this.canViewVendorCatalogs() ? this.pmsVendors : [];

    return this.respond(
      structuredClone(
        applyTableQuery(visibleVendors, query, {
          name: (vendor) => vendor.name
        })
      ),
      130
    );
  }

  override async listControllerVendorOptions(): Promise<ApiResponse<ControllerVendor[]>> {
    if (!this.canViewVendorCatalogs()) {
      return this.respond<ControllerVendor[]>(null, 110, {
        errorCode: 403,
        errorMessage: 'You are not allowed to view controller vendors.'
      });
    }

    return this.respond(structuredClone(this.controllerVendors), 110);
  }

  override async listPmsVendorOptions(): Promise<ApiResponse<PmsVendor[]>> {
    if (!this.canViewVendorCatalogs()) {
      return this.respond<PmsVendor[]>(null, 110, {
        errorCode: 403,
        errorMessage: 'You are not allowed to view PMS vendors.'
      });
    }

    return this.respond(structuredClone(this.pmsVendors), 110);
  }

  override async getControllerVendor(vendorId: string): Promise<ApiResponse<ControllerVendor>> {
    if (!this.canViewVendorCatalogs()) {
      return this.respond<ControllerVendor>(null, 110, {
        errorCode: 403,
        errorMessage: 'You are not allowed to view controller vendors.'
      });
    }

    return this.respond(
      structuredClone(this.controllerVendors.find((vendor) => vendor.id === vendorId) ?? null),
      110
    );
  }

  override async getPmsVendor(vendorId: string): Promise<ApiResponse<PmsVendor>> {
    if (!this.canViewVendorCatalogs()) {
      return this.respond<PmsVendor>(null, 110, {
        errorCode: 403,
        errorMessage: 'You are not allowed to view PMS vendors.'
      });
    }

    return this.respond(structuredClone(this.pmsVendors.find((vendor) => vendor.id === vendorId) ?? null), 110);
  }


  override async createTenant(
    draft: TenantDraft
  ): Promise<ApiResponse<DashboardSnapshot['tenants'][number]>> {
    const tenant = {
      id: this.createId(draft.name),
      name: draft.name.trim(),
      brand: draft.brand?.trim() ?? '',
      sla: draft.sla?.trim() ?? '',
      properties: 0,
      dpskCoverage: draft.dpskCoverage ?? 0,
      openAlerts: 0
    } satisfies DashboardSnapshot['tenants'][number];

    this.snapshot = {
      ...this.snapshot,
      tenants: [tenant, ...this.snapshot.tenants]
    };

    return this.respond(tenant, 180);
  }

  override async updateTenant(
    tenantId: string,
    draft: TenantDraft
  ): Promise<ApiResponse<DashboardSnapshot['tenants'][number]>> {
    const existing = this.snapshot.tenants.find((tenant) => tenant.id === tenantId);
    const tenant = {
      id: tenantId,
      name: draft.name.trim(),
      brand: draft.brand?.trim() || existing?.brand || '',
      sla: draft.sla?.trim() || existing?.sla || '',
      properties: existing?.properties ?? this.snapshot.locations.filter((location) => location.tenantId === tenantId).length,
      dpskCoverage: draft.dpskCoverage ?? existing?.dpskCoverage ?? 0,
      openAlerts: existing?.openAlerts ?? 0
    } satisfies DashboardSnapshot['tenants'][number];

    this.snapshot = {
      ...this.snapshot,
      tenants: this.snapshot.tenants.map((entry) => (entry.id === tenantId ? tenant : entry))
    };

    return this.respond(tenant, 180);
  }

  override async deleteTenant(tenantId: string): Promise<ApiResponse<{ id: string }>> {
    const locationIds = this.snapshot.locations
      .filter((location) => location.tenantId === tenantId)
      .map((location) => location.id);
    const unitIds = this.snapshot.spaces
      .filter((space) => locationIds.includes(space.locationId))
      .map((space) => space.id);

    this.snapshot = {
      ...this.snapshot,
      tenants: this.snapshot.tenants.filter((tenant) => tenant.id !== tenantId),
      locations: this.snapshot.locations.filter((location) => location.tenantId !== tenantId),
      spaces: this.snapshot.spaces.filter((space) => !locationIds.includes(space.locationId)),
      codes: this.snapshot.codes.filter(
        (code) => !locationIds.includes(code.locationId) && !unitIds.includes(code.spaceId)
      ),
      events: this.snapshot.events.filter((event) => !locationIds.includes(event.locationId)),
      bulkJobs: this.snapshot.bulkJobs.filter((job) => !locationIds.includes(job.locationId)),
      alerts: this.snapshot.alerts.filter((alert) => !locationIds.includes(alert.locationId)),
      users: this.snapshot.users
        .map((user) => ({
          ...user,
          scopes: user.scopes
            .filter((scope) => scope.tenantId !== tenantId)
            .map((scope) => ({
              ...scope,
              locationIds: scope.locationIds.filter((locationId) => !locationIds.includes(locationId))
            }))
        }))
        .filter((user) => user.role === 'platform-admin' || user.scopes.length > 0)
    };

    return this.respond({ id: tenantId }, 170);
  }

  override async createControllerVendor(
    draft: CreateControllerVendorRequest | ControllerVendorDraft
  ): Promise<ApiResponse<ControllerVendor>> {
    if (!this.canManageVendorCatalogs()) {
      return this.respond<ControllerVendor>(null, 140, {
        errorCode: 403,
        errorMessage: 'Only platform administrators can create controller vendors.'
      });
    }

    const vendor: ControllerVendor = {
      id: this.createId(draft.name),
      name: draft.name.trim(),
      fields: this.normalizeVendorFields(draft.fields)
    };

    if (this.controllerVendors.some((entry) => entry.id === vendor.id)) {
      return this.respond<ControllerVendor>(null, 140, {
        errorCode: 409,
        errorMessage: 'A controller vendor with the same generated identifier already exists.'
      });
    }

    this.controllerVendors = [vendor, ...this.controllerVendors];

    return this.respond(vendor, 140);
  }

  override async updateControllerVendor(
    vendorId: string,
    draft: UpdateControllerVendorRequest | ControllerVendorDraft
  ): Promise<ApiResponse<ControllerVendor>> {
    if (!this.canManageVendorCatalogs()) {
      return this.respond<ControllerVendor>(null, 140, {
        errorCode: 403,
        errorMessage: 'Only platform administrators can update controller vendors.'
      });
    }

    const generatedId = this.createId(draft.name);

    if (this.controllerVendors.some((entry) => entry.id !== vendorId && entry.id === generatedId)) {
      return this.respond<ControllerVendor>(null, 140, {
        errorCode: 409,
        errorMessage: 'A controller vendor with the same generated identifier already exists.'
      });
    }

    const vendor: ControllerVendor = {
      id: vendorId,
      name: draft.name.trim(),
      fields: this.normalizeVendorFields(draft.fields)
    };

    this.controllerVendors = this.controllerVendors.map((entry) =>
      entry.id === vendorId ? vendor : entry
    );

    return this.respond(vendor, 140);
  }

  override async deleteControllerVendor(vendorId: string): Promise<ApiResponse<{ id: string }>> {
    if (!this.canManageVendorCatalogs()) {
      return this.respond<{ id: string }>(null, 140, {
        errorCode: 403,
        errorMessage: 'Only platform administrators can delete controller vendors.'
      });
    }

    if (this.snapshot.locations.some((location) => location.controller?.vendorId === vendorId)) {
      return this.respond<{ id: string }>(null, 140, {
        errorCode: 409,
        errorMessage: 'The controller vendor is still referenced by one or more locations.'
      });
    }

    this.controllerVendors = this.controllerVendors.filter((vendor) => vendor.id !== vendorId);

    return this.respond({ id: vendorId }, 140);
  }

  override async createPmsVendor(
    draft: CreatePmsVendorRequest | PmsVendorDraft
  ): Promise<ApiResponse<PmsVendor>> {
    if (!this.canManageVendorCatalogs()) {
      return this.respond<PmsVendor>(null, 140, {
        errorCode: 403,
        errorMessage: 'Only platform administrators can create PMS vendors.'
      });
    }

    const vendor: PmsVendor = {
      id: this.createId(draft.name),
      name: draft.name.trim(),
      fields: this.normalizeVendorFields(draft.fields)
    };

    if (this.pmsVendors.some((entry) => entry.id === vendor.id)) {
      return this.respond<PmsVendor>(null, 140, {
        errorCode: 409,
        errorMessage: 'A PMS vendor with the same generated identifier already exists.'
      });
    }

    this.pmsVendors = [vendor, ...this.pmsVendors];

    return this.respond(vendor, 140);
  }

  override async updatePmsVendor(
    vendorId: string,
    draft: UpdatePmsVendorRequest | PmsVendorDraft
  ): Promise<ApiResponse<PmsVendor>> {
    if (!this.canManageVendorCatalogs()) {
      return this.respond<PmsVendor>(null, 140, {
        errorCode: 403,
        errorMessage: 'Only platform administrators can update PMS vendors.'
      });
    }

    const generatedId = this.createId(draft.name);

    if (this.pmsVendors.some((entry) => entry.id !== vendorId && entry.id === generatedId)) {
      return this.respond<PmsVendor>(null, 140, {
        errorCode: 409,
        errorMessage: 'A PMS vendor with the same generated identifier already exists.'
      });
    }

    const vendor: PmsVendor = {
      id: vendorId,
      name: draft.name.trim(),
      fields: this.normalizeVendorFields(draft.fields)
    };

    this.pmsVendors = this.pmsVendors.map((entry) => (entry.id === vendorId ? vendor : entry));

    return this.respond(vendor, 140);
  }

  override async deletePmsVendor(vendorId: string): Promise<ApiResponse<{ id: string }>> {
    if (!this.canManageVendorCatalogs()) {
      return this.respond<{ id: string }>(null, 140, {
        errorCode: 403,
        errorMessage: 'Only platform administrators can delete PMS vendors.'
      });
    }

    if (this.snapshot.locations.some((location) => location.pms?.vendorId === vendorId)) {
      return this.respond<{ id: string }>(null, 140, {
        errorCode: 409,
        errorMessage: 'The PMS vendor is still referenced by one or more locations.'
      });
    }

    this.pmsVendors = this.pmsVendors.filter((vendor) => vendor.id !== vendorId);

    return this.respond({ id: vendorId }, 140);
  }

  override async createLocation(
    tenantId: string,
    draft: CreateLocationRequest
  ): Promise<ApiResponse<DashboardSnapshot['locations'][number]>> {
    const pms = this.normalizeLocationVendorBinding(draft.pms, this.pmsVendors, 'PMS');
    const controller = this.normalizeLocationVendorBinding(
      draft.controller,
      this.controllerVendors,
      'controller'
    );

    if (!pms.ok) {
      return this.respond<DashboardSnapshot['locations'][number]>(null, 190, {
        errorCode: 400,
        errorMessage: pms.errorMessage
      });
    }

    if (!controller.ok) {
      return this.respond<DashboardSnapshot['locations'][number]>(null, 190, {
        errorCode: 400,
        errorMessage: controller.errorMessage
      });
    }

    const location = {
      id: this.createId(`${draft.name}-${draft.city}`),
      tenantId,
      ...draft,
      name: draft.name.trim(),
      city: draft.city.trim(),
      ssid: '',
      pms: pms.binding,
      controller: controller.binding,
      note: draft.note.trim(),
      timezone: draft.timezone.trim()
    } satisfies DashboardSnapshot['locations'][number];

    this.snapshot = {
      ...this.snapshot,
      locations: [location, ...this.snapshot.locations]
    };

    return this.respond(location, 190);
  }

  override async updateLocation(
    tenantId: string,
    locationId: string,
    draft: UpdateLocationRequest
  ): Promise<ApiResponse<DashboardSnapshot['locations'][number]>> {
    const pms = this.normalizeLocationVendorBinding(draft.pms, this.pmsVendors, 'PMS');
    const controller = this.normalizeLocationVendorBinding(
      draft.controller,
      this.controllerVendors,
      'controller'
    );

    if (!pms.ok) {
      return this.respond<DashboardSnapshot['locations'][number]>(null, 190, {
        errorCode: 400,
        errorMessage: pms.errorMessage
      });
    }

    if (!controller.ok) {
      return this.respond<DashboardSnapshot['locations'][number]>(null, 190, {
        errorCode: 400,
        errorMessage: controller.errorMessage
      });
    }

    const location = {
      id: locationId,
      tenantId,
      ...draft,
      name: draft.name.trim(),
      city: draft.city.trim(),
      ssid: this.locationSsid(locationId),
      pms: pms.binding,
      controller: controller.binding,
      note: draft.note.trim(),
      timezone: draft.timezone.trim()
    } satisfies DashboardSnapshot['locations'][number];

    this.snapshot = {
      ...this.snapshot,
      locations: this.snapshot.locations.map((entry) => (entry.id === locationId ? location : entry))
    };

    return this.respond(location, 190);
  }

  override async deleteLocation(
    tenantId: string,
    locationId: string
  ): Promise<ApiResponse<{ id: string }>> {
    if (!this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond({ id: locationId }, 160);
    }

    const unitIds = this.snapshot.spaces
      .filter((space) => space.locationId === locationId)
      .map((space) => space.id);

    this.snapshot = {
      ...this.snapshot,
      locations: this.snapshot.locations.filter((entry) => entry.id !== locationId),
      spaces: this.snapshot.spaces.filter((space) => space.locationId !== locationId),
      codes: this.snapshot.codes.filter(
        (code) => code.locationId !== locationId && !unitIds.includes(code.spaceId)
      ),
      events: this.snapshot.events.filter((event) => event.locationId !== locationId),
      bulkJobs: this.snapshot.bulkJobs.filter((job) => job.locationId !== locationId),
      alerts: this.snapshot.alerts.filter((alert) => alert.locationId !== locationId),
      users: this.snapshot.users.map((user) => ({
        ...user,
        scopes: user.scopes
          .map((scope) => ({
            ...scope,
            locationIds: scope.locationIds.filter((id) => id !== locationId)
          }))
          .filter(
            (scope) =>
              scope.locationIds.length > 0 ||
              user.role === 'tenant-admin' ||
              user.role === 'platform-admin'
          )
      }))
    };

    return this.respond({ id: locationId }, 160);
  }

  override async bulkCreateLocations(
    tenantId: string,
    request: BulkCreateLocationsRequest
  ): Promise<ApiResponse<{ created: number; failed: number; items: DashboardSnapshot['locations'] }>> {
    const items: DashboardSnapshot['locations'] = [];
    let failed = 0;

    for (const draft of request.items) {
      const response = await this.createLocation(tenantId, draft);
      if (response.data) {
        items.push(response.data);
      } else {
        failed += 1;
      }
    }

    return this.respond({ created: items.length, failed, items }, 220);
  }

  override async createUser(draft: CreateUserRequest): Promise<ApiResponse<AppUser>> {
    const requestedRole = draft.role;

    if (!requestedRole || !this.canAssignRole(requestedRole)) {
      return this.respond<AppUser>(null, 180);
    }

    const user: AppUser = {
      id: this.createId(draft.email),
      name: draft.name.trim(),
      email: draft.email.trim(),
      role: requestedRole,
      scopes: this.normalizeUserRequestScopes(draft),
      status: draft.status
    };

    this.snapshot = {
      ...this.snapshot,
      users: [user, ...this.snapshot.users]
    };

    return this.respond(user, 180);
  }

  override async updateUser(userId: string, draft: UpdateUserRequest): Promise<ApiResponse<AppUser>> {
    const requestedRole = draft.role;

    if (!requestedRole || !this.canAssignRole(requestedRole)) {
      return this.respond<AppUser>(null, 180);
    }

    const user: AppUser = {
      id: userId,
      name: draft.name.trim(),
      email: draft.email.trim(),
      role: requestedRole,
      scopes: this.normalizeUserRequestScopes(draft),
      status: draft.status
    };

    this.snapshot = {
      ...this.snapshot,
      users: this.snapshot.users.map((entry) => (entry.id === userId ? user : entry))
    };

    return this.respond(user, 180);
  }

  override async deleteUser(userId: string): Promise<ApiResponse<{ id: string }>> {
    this.snapshot = {
      ...this.snapshot,
      users: this.snapshot.users.filter((user) => user.id !== userId)
    };

    return this.respond({ id: userId }, 150);
  }

  override async createUnit(
    tenantId: string,
    locationId: string,
    draft: CreateUnitRequest
  ): Promise<ApiResponse<Space>> {
    const unit: Space = {
      id: this.createId(`${locationId}-${draft.name}`),
      locationId,
      ...draft,
      serviceVlanId: draft.type === 'room' ? null : draft.serviceVlanId ?? null,
      networkId: draft.networkId ?? null,
      name: draft.name.trim()
    };

    if (!this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond<Space>(null, 170);
    }

    this.snapshot = {
      ...this.snapshot,
      spaces: [unit, ...this.snapshot.spaces]
    };

    return this.respond(unit, 170);
  }

  override async updateUnit(
    tenantId: string,
    locationId: string,
    spaceId: string,
    draft: UpdateUnitRequest
  ): Promise<ApiResponse<Space>> {
    const unit: Space = {
      id: spaceId,
      locationId,
      ...draft,
      serviceVlanId: draft.type === 'room' ? null : draft.serviceVlanId ?? null,
      networkId: draft.networkId ?? null,
      name: draft.name.trim()
    };

    if (!this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond<Space>(null, 170);
    }

    this.snapshot = {
      ...this.snapshot,
      spaces: this.snapshot.spaces.map((entry) => (entry.id === spaceId ? unit : entry))
    };

    return this.respond(unit, 170);
  }

  override async deleteUnit(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<{ id: string }>> {
    if (!this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond({ id: spaceId }, 150);
    }

    this.snapshot = {
      ...this.snapshot,
      spaces: this.snapshot.spaces.filter((space) => space.id !== spaceId),
      codes: this.snapshot.codes.filter((code) => code.spaceId !== spaceId)
    };

    return this.respond({ id: spaceId }, 150);
  }

  override async bulkCreateUnits(
    tenantId: string,
    locationId: string,
    request: BulkCreateUnitsRequest
  ): Promise<ApiResponse<{ created: number; failed: number; items: Space[] }>> {
    const items: Space[] = [];
    let failed = 0;

    for (const draft of request.items) {
      const response = await this.createUnit(tenantId, locationId, draft);
      if (response.data) {
        items.push(response.data);
      } else {
        failed += 1;
      }
    }

    return this.respond({ created: items.length, failed, items }, 210);
  }

  override async createManualCode(
    tenantId: string,
    locationId: string,
    draft: Omit<ManualCodeDraft, 'locationId'>,
    spaces: Space[]
  ): Promise<ApiResponse<AccessCode>> {
    const space = spaces.find((entry) => entry.id === draft.spaceId);
    if (!space) {
      return this.respond<AccessCode>(null, 120, {
        errorCode: 422,
        errorMessage: 'A space is required for manual codes.'
      });
    }

    if (draft.serviceMode && space?.type !== 'event-hall') {
      return this.respond<AccessCode>(null, 120, {
        errorCode: 422,
        errorMessage: 'Service mode is available only for event halls.'
      });
    }

    const useServiceVlan = draft.serviceMode || draft.category === 'relator';
    const passphrase = this.createPassphrase(
      useServiceVlan ? space?.serviceVlanId ?? space?.vlanId ?? 9999 : space?.vlanId ?? 9999
    );
    const code = {
      id: `DPSK-${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
      guestName: `${draft.firstName} ${draft.lastName}`.trim(),
      email: draft.email,
      locationId,
      spaceId: draft.spaceId,
      source: 'reception',
      deliveryChannels: draft.deliveryChannels,
      status: new Date(draft.validFrom) > new Date() ? 'scheduled' : 'active',
      passphrase,
      validFrom: draft.validFrom,
      validUntil: draft.validUntil,
      devicesAllowed: draft.devicesAllowed,
      devicesConnected: 0,
      qrPayload: wifiPayload(draft.ssid || this.locationSsid(locationId) || WIFI_PRIMARY, passphrase),
      bookingReference: draft.bookingReference || `MAN-${Date.now().toString().slice(-6)}`,
      category: draft.category ?? 'guest',
      serviceMode: useServiceVlan,
      tags: [space?.name ?? 'manual', 'reception'],
      notes: draft.notes ?? null
    } satisfies AccessCode;

    if (!this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond<AccessCode>(null, 420);
    }

    this.snapshot = {
      ...this.snapshot,
      codes: [code, ...this.snapshot.codes]
    };

    return this.respond(code, 420);
  }

  override async createGroupCode(
    tenantId: string,
    locationId: string,
    spaceId: string,
    draft: GroupCodeDraft
  ): Promise<ApiResponse<GroupAccessCode>> {
    const space = this.snapshot.spaces.find(
      (entry) => entry.id === spaceId && entry.locationId === locationId
    );

    if (
      !this.locationBelongsToTenant(locationId, tenantId) ||
      !space ||
      (space.type !== 'event-hall' && space.type !== 'public-area')
    ) {
      return this.respond<GroupAccessCode>(null, 180, {
        errorCode: 422,
        errorMessage: 'Group codes require an event hall or public area.'
      });
    }

    const useServiceVlan = draft.serviceMode || draft.category === 'relator';
    const vlan = useServiceVlan ? space.serviceVlanId ?? space.vlanId : space.vlanId;
    const passphrase = this.createPassphrase(vlan);
    const code: GroupAccessCode = {
      id: `DPSK-${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
      groupName: draft.groupName.trim(),
      contactName: draft.contactName.trim(),
      contactEmail: draft.contactEmail.trim(),
      locationId,
      spaceId,
      source: 'reception',
      category: draft.category,
      deliveryChannels: draft.deliveryChannels,
      status: new Date(draft.validFrom) > new Date() ? 'scheduled' : 'active',
      passphrase,
      validFrom: draft.validFrom,
      validUntil: draft.validUntil,
      devicesAllowed: draft.devicesAllowed,
      devicesConnected: 0,
      lastSeenAt: null,
      qrPayload: wifiPayload(draft.ssid || this.locationSsid(locationId) || WIFI_EVENTS, passphrase),
      serviceMode: useServiceVlan,
      tags: [draft.groupName.trim()],
      notes: draft.notes?.trim() || null
    };

    return this.respond(code, 240);
  }

  override async extendCode(
    tenantId: string,
    locationId: string,
    codeId: string,
    hours: number
  ): Promise<ApiResponse<AccessCode>> {
    const current = this.snapshot.codes.find(
      (code) =>
        code.id === codeId &&
        code.locationId === locationId &&
        this.locationBelongsToTenant(locationId, tenantId)
    );

    if (!current) {
      return this.respond<AccessCode>(null, 220);
    }

    const nextValidUntil = new Date(current.validUntil);
    nextValidUntil.setHours(nextValidUntil.getHours() + hours);

    const updated = {
      ...current,
      validUntil: nextValidUntil.toISOString(),
      status: nextValidUntil > new Date() ? 'active' : 'expired'
    } satisfies AccessCode;

    this.snapshot = {
      ...this.snapshot,
      codes: this.snapshot.codes.map((code) => (code.id === codeId ? updated : code))
    };

    return this.respond(updated, 220);
  }

  override async revokeCode(
    tenantId: string,
    locationId: string,
    codeId: string
  ): Promise<ApiResponse<AccessCode>> {
    const current = this.snapshot.codes.find(
      (code) =>
        code.id === codeId &&
        code.locationId === locationId &&
        this.locationBelongsToTenant(locationId, tenantId)
    );

    if (!current) {
      return this.respond<AccessCode>(null, 220);
    }

    const updated = {
      ...current,
      status: 'revoked',
      devicesConnected: 0
    } satisfies AccessCode;

    this.snapshot = {
      ...this.snapshot,
      codes: this.snapshot.codes.map((code) => (code.id === codeId ? updated : code))
    };

    return this.respond(updated, 220);
  }

  override async sendCodeEmail(
    tenantId: string,
    locationId: string,
    codeId: string
  ): Promise<ApiResponse<{ codeId?: string; channel: 'email'; deliveredAt: string }>> {
    const code = this.snapshot.codes.find(
      (entry) => entry.id === codeId && entry.locationId === locationId
    );
    if (!code || !this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond(null, 120, { errorCode: 404, errorMessage: 'Code not found.' });
    }
    return this.respond({ codeId, channel: 'email', deliveredAt: new Date().toISOString() }, 180);
  }

  override async createEvent(
    tenantId: string,
    locationId: string,
    draft: HospitalityEventDraft
  ): Promise<ApiResponse<HospitalityEvent>> {
    if (!this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond(null, 160, { errorCode: 404, errorMessage: 'Location not found.' });
    }
    const event: HospitalityEvent = {
      ...draft,
      id: `EVT-${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
      tenantId,
      locationId,
      code: this.createPassphrase(
        this.snapshot.spaces.find((space) => space.id === draft.spaceId)?.vlanId ?? 9999
      ),
      status: new Date(draft.validFrom) > new Date() ? 'scheduled' : 'active'
    };
    this.snapshot = { ...this.snapshot, events: [event, ...this.snapshot.events] };
    return this.respond(event, 260);
  }

  override async updateEvent(
    tenantId: string,
    locationId: string,
    eventId: string,
    draft: HospitalityEventDraft
  ): Promise<ApiResponse<HospitalityEvent>> {
    const current = this.snapshot.events.find((event) => event.id === eventId);
    if (!current || !this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond(null, 160, { errorCode: 404, errorMessage: 'Event not found.' });
    }
    const updated: HospitalityEvent = {
      ...current,
      ...draft,
      status: new Date(draft.validUntil) < new Date()
        ? 'expired'
        : new Date(draft.validFrom) > new Date() ? 'scheduled' : 'active'
    };
    this.snapshot = {
      ...this.snapshot,
      events: this.snapshot.events.map((event) => event.id === eventId ? updated : event)
    };
    return this.respond(updated, 220);
  }

  override async disableEvent(
    tenantId: string,
    locationId: string,
    eventId: string
  ): Promise<ApiResponse<HospitalityEvent>> {
    const current = this.snapshot.events.find((event) => event.id === eventId);
    if (!current || !this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond(null, 140, { errorCode: 404, errorMessage: 'Event not found.' });
    }
    const updated: HospitalityEvent = { ...current, status: 'disabled' };
    this.snapshot = {
      ...this.snapshot,
      events: this.snapshot.events.map((event) => event.id === eventId ? updated : event)
    };
    return this.respond(updated, 180);
  }

  override async sendEventEmail(
    tenantId: string,
    locationId: string,
    eventId: string
  ): Promise<ApiResponse<{ eventId?: string; channel: 'email'; deliveredAt: string }>> {
    const event = this.snapshot.events.find((entry) => entry.id === eventId);
    if (!event || !this.locationBelongsToTenant(locationId, tenantId)) {
      return this.respond(null, 120, { errorCode: 404, errorMessage: 'Event not found.' });
    }
    return this.respond({ eventId, channel: 'email', deliveredAt: new Date().toISOString() }, 180);
  }

  override async createBulkJob(
    locationId: string,
    draft: Omit<BulkUploadDraft, 'locationId'>,
    spaces: Space[]
  ): Promise<ApiResponse<{ job: BulkJob; codes: AccessCode[] }>> {
    const parsedRows = this.parseCsv(draft.csvText);
    const fallbackSpace = spaces.find((space) => space.id === draft.defaultSpaceId) ?? spaces[0];

    const codes = parsedRows.map((row, index) => {
      const matchingSpace =
        spaces.find((space) => space.id === (row['spaceId'] || draft.defaultSpaceId)) ??
        fallbackSpace;
      const useServiceVlan = draft.serviceMode || row['category'] === 'relator';
      const baseVlan = useServiceVlan
        ? fallbackSpace?.serviceVlanId ?? fallbackSpace?.vlanId ?? 9999
        : fallbackSpace?.vlanId ?? 9999;
      const vlan = draft.mode === 'shared-vlan'
        ? baseVlan
        : matchingSpace?.vlanId ?? baseVlan;
      const passphrase = this.createPassphrase(vlan + index);

      return {
        id: `DPSK-${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
        guestName: `${row['firstName']} ${row['lastName']}`.trim(),
        email: row['email'],
        locationId,
        spaceId:
          draft.mode === 'shared-vlan'
            ? fallbackSpace?.id ?? matchingSpace?.id ?? ''
            : matchingSpace?.id ?? fallbackSpace?.id ?? '',
        source: 'bulk',
        category: row['category'] === 'guest' || row['category'] === 'visitor' ||
          row['category'] === 'iot' || row['category'] === 'staff' ||
          row['category'] === 'journalist' || row['category'] === 'relator'
          ? row['category']
          : null,
        deliveryChannels: draft.deliveryChannels,
        status: new Date(row['validUntil']) > new Date() ? 'active' : 'expired',
        passphrase,
        validFrom: row['validFrom'],
        validUntil: row['validUntil'],
        devicesAllowed: Number.parseInt(row['devicesAllowed'], 10) || 3,
        devicesConnected: 0,
        qrPayload: wifiPayload(draft.ssid || this.locationSsid(locationId) || WIFI_EVENTS, passphrase),
        bookingReference: row['bookingReference'] || `BULK-${index + 1}`,
        serviceMode: useServiceVlan,
        tags: [draft.mode, matchingSpace?.name ?? 'bulk-import'],
        notes: null
      } satisfies AccessCode;
    });

    const total = codes.length;
    const job: BulkJob = {
      id: `BLK-${Date.now().toString().slice(-8)}`,
      locationId,
      fileName: `bulk-${locationId}.csv`,
      requestedBy: draft.requestedBy,
      createdAt: new Date().toISOString().slice(0, 16),
      total,
      processed: total > 10 ? Math.floor(total * 0.7) : total,
      mode: draft.mode,
      queueStatus: total > 10 ? 'running' : 'completed',
      targetVlan: fallbackSpace?.vlanId
    };

    this.snapshot = {
      ...this.snapshot,
      bulkJobs: [job, ...this.snapshot.bulkJobs],
      codes: [...codes, ...this.snapshot.codes]
    };

    return this.respond({ job, codes }, 680);
  }

  override async generateQrCode(payload: string): Promise<string> {
    return this.activity.track(() =>
      QRCode.toDataURL(payload, {
        margin: 1,
        width: 320,
        color: {
          dark: '#102033',
          light: '#FBF7EF'
        }
      })
    );
  }

  private parseCsv(csvText: string): Array<Record<string, string>> {
    const rows = csvText
      .trim()
      .split(/\r?\n/)
      .filter(Boolean);

    if (rows.length < 2) {
      return [];
    }

    const headers = this.splitCsvLine(rows[0]);

    return rows.slice(1).map((row) => {
      const values = this.splitCsvLine(row);

      return headers.reduce<Record<string, string>>((record, header, index) => {
        record[header] = values[index] ?? '';
        return record;
      }, {});
    });
  }

  private splitCsvLine(line: string): string[] {
    return line.split(',').map((cell) => cell.trim());
  }

  private createPassphrase(vlanId: number): string {
    const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `LMN-${vlanId}-${suffix}`;
  }

  private locationBelongsToTenant(locationId: string, tenantId: string): boolean {
    return this.visibleSnapshot().locations.some(
      (location) => location.id === locationId && location.tenantId === tenantId
    );
  }

  private visibleSnapshot(): DashboardSnapshot {
    const role = this.currentRole();

    if (!role || role === 'platform-admin' || role === 'operations') {
      return this.snapshot;
    }

    const { tenantIds, locationIds } = this.currentScope();

    if (!tenantIds.size) {
      return this.emptySnapshot();
    }

    const limitByLocation = locationIds.size > 0;
    const locations = this.snapshot.locations.filter(
      (location) =>
        tenantIds.has(location.tenantId) && (!limitByLocation || locationIds.has(location.id))
    );
    const visibleLocationIds = new Set(locations.map((location) => location.id));
    const visibleTenantIds = new Set(locations.map((location) => location.tenantId));
    const spaces = this.snapshot.spaces.filter((space) => visibleLocationIds.has(space.locationId));
    const codes = this.snapshot.codes.filter((code) => visibleLocationIds.has(code.locationId));
    const events = this.snapshot.events.filter((event) => visibleLocationIds.has(event.locationId));
    const bulkJobs = this.snapshot.bulkJobs.filter((job) => visibleLocationIds.has(job.locationId));
    const alerts = this.snapshot.alerts.filter((alert) => visibleLocationIds.has(alert.locationId));
    const users = this.snapshot.users.filter((user) => {
      if (role === 'tenant-admin' && user.role === 'platform-admin') {
        return false;
      }

      const userTenantScope = userTenantIds(user.scopes);
      const tenantMatch = userTenantScope.some((tenantId) => visibleTenantIds.has(tenantId));

      if (!tenantMatch) {
        return false;
      }

      const userLocationScope = userLocationIds(user.scopes);

      return !limitByLocation || userLocationScope.length === 0 || userLocationScope.some((id) => visibleLocationIds.has(id));
    });

    return {
      tenants: this.snapshot.tenants.filter((tenant) => visibleTenantIds.has(tenant.id)),
      locations,
      spaces,
      codes,
      events,
      bulkJobs,
      users,
      alerts
    };
  }

  private currentRole(): UserRole | null {
    return normalizeUserRole(this.auth?.user()?.role);
  }

  private currentScope(): { tenantIds: Set<string>; locationIds: Set<string> } {
    const authUser = this.auth?.user();
    const matchedUser =
      authUser &&
      (this.snapshot.users.find(
        (user) =>
          user.id === authUser.id ||
          (!!user.email && !!authUser.email && user.email.toLowerCase() === authUser.email.toLowerCase())
      ) ??
        null);
    const tenantIds = matchedUser?.scopes.length
      ? userTenantIds(matchedUser.scopes)
      : authUser?.tenantIds ?? [];
    const locationIds = matchedUser?.scopes.length
      ? userLocationIds(matchedUser.scopes)
      : authUser?.locationIds ?? [];

    return {
      tenantIds: new Set(tenantIds),
      locationIds: new Set(locationIds)
    };
  }

  private normalizeUserScopes(scopes: AppUser['scopes']): AppUser['scopes'] {
    return [...new Map(scopes.map((scope) => [scope.tenantId, scope])).values()].map((scope) => ({
      tenantId: scope.tenantId,
      locationIds: [...new Set(scope.locationIds)]
    }));
  }

  private normalizeUserRequestScopes(draft: CreateUserRequest | UpdateUserRequest): AppUser['scopes'] {
    const visibleTenants = new Set(this.visibleSnapshot().tenants.map((tenant) => tenant.id));
    const visibleLocations = new Set(this.visibleSnapshot().locations.map((location) => location.id));

    return this.normalizeUserScopes(draft.scopes)
      .filter((scope) => visibleTenants.has(scope.tenantId))
      .map((scope) => ({
        tenantId: scope.tenantId,
        locationIds: scope.locationIds.filter((locationId) => visibleLocations.has(locationId))
      }))
      .filter(
        (scope) =>
          scope.locationIds.length > 0 ||
          draft.role === 'tenant-admin' ||
          draft.role === 'platform-admin'
      );
  }

  private canAssignRole(role: UserRole): boolean {
    const currentRole = this.currentRole();

    if (!currentRole || currentRole === 'platform-admin') {
      return true;
    }

    if (currentRole === 'tenant-admin') {
      return role !== 'platform-admin';
    }

    return false;
  }

  private canViewVendorCatalogs(): boolean {
    const currentRole = this.currentRole();

    return !currentRole || currentRole === 'platform-admin' || currentRole === 'tenant-admin';
  }

  private canManageVendorCatalogs(): boolean {
    const currentRole = this.currentRole();

    return !currentRole || currentRole === 'platform-admin';
  }

  private controllerVendorName(binding: VendorBinding | null | undefined): string {
    if (!binding?.vendorId) {
      return '';
    }

    return this.controllerVendors.find((vendor) => vendor.id === binding.vendorId)?.name ?? binding.vendorId;
  }

  private pmsVendorName(binding: VendorBinding | null | undefined): string {
    if (!binding?.vendorId) {
      return '';
    }

    return this.pmsVendors.find((vendor) => vendor.id === binding.vendorId)?.name ?? binding.vendorId;
  }

  private normalizeLocationVendorBinding(
    binding: VendorBinding | null | undefined,
    vendors: ControllerVendor[] | PmsVendor[],
    vendorLabel: string
  ):
    | { ok: true; binding: VendorBinding | null }
    | {
        ok: false;
        errorMessage: string;
      } {
    const vendorId = binding?.vendorId.trim() ?? '';

    if (!vendorId) {
      return { ok: true, binding: null };
    }

    const vendor = vendors.find((entry) => entry.id === vendorId);

    if (!vendor) {
      return {
        ok: false,
        errorMessage: `${vendorLabel} vendor "${vendorId}" is not registered.`
      };
    }

    const normalizedConfiguration: VendorConfiguration = {};

    for (const [fieldName, fieldType] of Object.entries(vendor.fields)) {
      const rawValue = binding?.configuration?.[fieldName];

      if (rawValue === undefined || rawValue === null || rawValue === '') {
        return {
          ok: false,
          errorMessage: `${vendor.name}: configuration field "${fieldName}" is required.`
        };
      }

      const normalizedValue = this.normalizeVendorConfigurationValue(rawValue, fieldType);

      if (normalizedValue === undefined) {
        return {
          ok: false,
          errorMessage: `${vendor.name}: configuration field "${fieldName}" must be of type ${fieldType}.`
        };
      }

      normalizedConfiguration[fieldName] = normalizedValue;
    }

    return {
      ok: true,
      binding: {
        vendorId,
        configuration: normalizedConfiguration
      }
    };
  }

  private normalizeVendorConfigurationValue(
    rawValue: VendorConfigurationValue,
    fieldType: ControllerVendor['fields'][string] | PmsVendor['fields'][string]
  ): VendorConfigurationValue | undefined {
    if (fieldType === 'boolean') {
      if (typeof rawValue === 'boolean') {
        return rawValue;
      }

      if (rawValue === 'true') {
        return true;
      }

      if (rawValue === 'false') {
        return false;
      }

      return undefined;
    }

    if (fieldType === 'number') {
      if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
        return rawValue;
      }

      if (typeof rawValue === 'string') {
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : undefined;
      }

      return undefined;
    }

    if (typeof rawValue !== 'string') {
      return String(rawValue);
    }

    const trimmed = rawValue.trim();
    return trimmed ? trimmed : undefined;
  }

  private normalizeVendorFields(
    fields: ControllerVendorDraft['fields'] | PmsVendorDraft['fields']
  ): ControllerVendor['fields'] {
    return Object.fromEntries(
      Object.entries(fields)
        .map(([key, value]) => [key.trim(), value] as const)
        .filter(([key]) => !!key)
    );
  }

  private createId(value: string): string {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return normalized || crypto.randomUUID().slice(0, 8);
  }

  private emptySnapshot(): DashboardSnapshot {
    return {
      tenants: [],
      locations: [],
      spaces: [],
      codes: [],
      events: [],
      bulkJobs: [],
      users: [],
      alerts: []
    };
  }

  private spaceName(spaceId: string): string {
    return this.snapshot.spaces.find((space) => space.id === spaceId)?.name ?? '';
  }

  private locationSsid(locationId: string): string {
    return (
      this.snapshot.locations.find((location) => location.id === locationId)?.ssid?.trim() ||
      this.snapshot.spaces.find((space) => space.locationId === locationId)?.ssid?.trim() ||
      ''
    );
  }

  private userTenantNames(user: AppUser): string {
    return this.visibleSnapshot().tenants
      .filter((tenant) => userTenantIds(user.scopes).includes(tenant.id))
      .map((tenant) => tenant.name)
      .join(', ');
  }

  private userLocationNames(user: AppUser): string {
    return this.visibleSnapshot().locations
      .filter((location) => userLocationIds(user.scopes).includes(location.id))
      .map((location) => location.name)
      .join(', ');
  }

  private async respond<T>(
    payload: T | null,
    timeout = 180,
    error: { errorCode: number; errorMessage: string | null } | null = null
  ): Promise<ApiResponse<T>> {
    return this.activity.track(async () => {
      await new Promise((resolve) => window.setTimeout(resolve, timeout));
      return {
        success: payload !== null,
        errorCode: payload !== null ? 0 : error?.errorCode ?? 404,
        errorMessage: payload !== null ? null : error?.errorMessage ?? 'Resource not found.',
        elapsed: timeout,
        data: payload
      };
    });
  }

  private async withDelay<T>(payload: T, timeout = 180): Promise<T> {
    return this.activity.track(async () => {
      await new Promise((resolve) => window.setTimeout(resolve, timeout));
      return payload;
    });
  }
}
